/**
 * VersionCheckContext
 *
 * Thin provider that calls useVersionCheck() exactly once and exposes its
 * full return value to descendant consumers (both SecurityUpdateController
 * and the legacy UpdateNotification banner). Owning the polling lifecycle
 * here ensures we have a single AbortController, a single timer, and a
 * single fetch cycle — not one per consumer.
 *
 * Must wrap BOTH the security gate AND the legacy banner so the lock
 * decision and the banner-suppression decision read from the same source
 * of truth.
 */

import { createContext, useContext } from 'react';
import PropTypes from 'prop-types';
import { useVersionCheck } from '../hooks/useVersionCheck';

const noop = () => {};

const VersionCheckContext = createContext({
  updateAvailable: false,
  newVersion: null,
  newBuildTime: null,
  currentVersion: '',
  applyUpdate: noop,
  dismissUpdate: noop,
  securityState: 'none',
  securityManifest: {},
});

export const VersionCheckProvider = ({ children }) => {
  const value = useVersionCheck();
  return (
    <VersionCheckContext.Provider value={value}>
      {children}
    </VersionCheckContext.Provider>
  );
};

VersionCheckProvider.propTypes = {
  children: PropTypes.node,
};

export const useVersionCheckContext = () => useContext(VersionCheckContext);
